import { Component, DestroyRef, computed, inject, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { IconName, IconNames } from '@icon/assets';
import { UiButtonComponent, UIIconComponent } from '@design-system';
import { interval } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import type { HeaderSymbol } from '../header/events/header-events.model';
import { AppClockService } from './app-clock.service';
import { TradingTimeBottomSheetComponent } from './components/trading-time-bottom-sheet/trading-time-bottom-sheet.component';
import { TradingTimeService } from '../../services/trading-time.service';
import { MarketClockState } from './app-clock.model';
import { DEFAULT_MARKET_GROUP_ID, marketGroupFor, marketScheduleForToday } from './utils/clock.utils';

interface ClockStateStyle {
	icon: IconNames;
	colorClass: string;
	tooltip: string;
}

const CLOCK_STATE_STYLE: Record<MarketClockState, ClockStateStyle> = {
	closed: { icon: IconName.ClockError, colorClass: 'text-red-500', tooltip: 'بازار بسته است' },
	preTrading: { icon: IconName.Clock, colorClass: 'text-yellow-500', tooltip: 'پیش گشایش' },
	trading: { icon: IconName.ClockCheck, colorClass: 'text-green-500', tooltip: 'بازار باز است' }
};

@Component({
	selector: 'app-clock',
	templateUrl: './app-clock.component.html',
	imports: [UIIconComponent, UiButtonComponent, TradingTimeBottomSheetComponent],
	host: {
		class: 'flex shrink-0'
	}
})
export class AppClockComponent {
	private readonly clockService = inject(AppClockService);
	private readonly tradingTimeService = inject(TradingTimeService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly tradingTimeSheet = viewChild<TradingTimeBottomSheetComponent>('tradingTime');

	readonly symbol = input<HeaderSymbol | null>(null);

	/** Last time received from the SSE `clock` topic; authoritative, and restarts the local ticker below. */
	private readonly serverTime = signal(new Date());
	readonly currentTime = signal(new Date());

	private readonly marketGroup = computed(() =>
		marketGroupFor(this.tradingTimeService.marketGroups(), this.symbol()?.groupId ?? DEFAULT_MARKET_GROUP_ID)
	);
	private readonly symbolState = computed<MarketClockState>(
		() => marketScheduleForToday(this.marketGroup(), this.currentTime()).state
	);

	// 'en-US' rather than the device locale: the digits are Latin in the DOM and
	// become Persian only when «نمایش اعداد فارسی» is on, like every other figure.
	// The default locale would print ۱۲:۳۰ on a fa-IR phone whatever the setting says.
	clock = computed(() =>
		this.currentTime().toLocaleTimeString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: false
		})
	);

	private readonly style = computed(() => CLOCK_STATE_STYLE[this.symbolState()]);
	iconName = computed(() => this.style().icon);
	colorClass = computed(() => this.style().colorClass);
	tooltip = computed(() => this.style().tooltip);

	openTradingTime() {
		this.tradingTimeSheet()?.open();
	}

	constructor() {
		// The SSE topic only pushes every few seconds; the server value always wins immediately,
		// and also becomes the new baseline for the local per-second ticker below.
		this.clockService
			.connect()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(time => {
				this.currentTime.set(time);
				this.serverTime.set(time);
			});

		// Ticks the displayed time forward every second between SSE updates. Each new
		// server time restarts this ticker from that authoritative baseline via switchMap.
		toObservable(this.serverTime)
			.pipe(
				switchMap(baseline =>
					interval(1000).pipe(map(tick => new Date(baseline.getTime() + (tick + 1) * 1000)))
				),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(time => this.currentTime.set(time));
	}
}
